import { useGlobalErrorStore } from "@/store/useGlobalError";
import { pb } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";
import type { Ref } from "vue";

export const mapSelectionToMinutes = (selectedValue: any): number => {
  selectedValue = selectedValue.toString();

  if (!selectedValue.includes(".")) return Number(selectedValue) * 60;

  const [hours = "0", minutes = "0"] = selectedValue.split(".").map(Number);
  return hours * 60 + minutes;
};

export const formatDurationValue = (totalMinutes: number, wholeHoursOnly = false): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return wholeHoursOnly && minutes === 0
    ? String(hours)
    : `${hours}:${minutes.toString().padStart(2, "0")}`;
};

export const formatDurationLabel = (totalMinutes: number, includeZeroMinutes = false): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const hoursText = hours > 0 ? `${hours} ${hours > 1 ? "hours" : "hour"}` : "";
  const minutesText = minutes > 0 || includeZeroMinutes ? `${minutes.toString().padStart(2, "0")} minutes` : "";

  return [hoursText, minutesText].filter(Boolean).join(" ");
};

export const handleMembersError = (error: any) => {
  const { handleUpdateErrorMessage } = useGlobalErrorStore();
  handleUpdateErrorMessage(error.isVisible, error.message);
};

export const handleDateTimeError = (error: any) => {
  const { handleUpdateErrorMessage } = useGlobalErrorStore();
  handleUpdateErrorMessage(error.isVisible, error.message, "warning", "tabler-alert-triangle");
};

export const getMaxActivityVisitors = (activityList: any[], activityId: string): number => {
  return activityList?.find((item: any) => item.id === activityId)?.maxPlayers;
};

export const getMembershipDiscounts = async (owner: string | undefined, requireAuth?: boolean) => {
  if (requireAuth && !pb.authStore?.model?.id) {
    return {};
  }
  if (owner) {
    const userMembershipData = await pb.collection("users").getOne(owner, { expand: "membership", requestKey: null });
    if (userMembershipData?.expand?.membership && !userMembershipData?.preventBenefits) {
      return userMembershipData?.expand?.membership;
    }
  }
  return {};
};

export const doesUserHaveCoupons = async (userId: string, afterHours?: boolean, activityId?: string) => {
  if(!userId) return false;
  let user;
  try {
    user = await pb.collection('users').getOne(userId);
  } catch (e) {
    return false;
  }
  if(user){
    let filter = 'user="' + userId + '" && qtyRemaining > 0 && expiryTime >= @now'
    if(!afterHours){
      filter += ' && afterHoursOnly = false'
    }

    let couponCheck = await pb.collection("coupons").getFullList({filter, requestKey: null})

    if(couponCheck && couponCheck.length > 0){
      const preventBenefits = user?.preventBenefits
      let validCoupons = preventBenefits || user?.membership === ''
        ? couponCheck.filter((c: any) => c.name !== 'Membership Join' && c.name !== 'Membership Renew')
        : couponCheck
      if(activityId){
        validCoupons = validCoupons.filter((c: any) => !c.activity?.length || c.activity.includes(activityId))
      }
      return validCoupons.length > 0
    }
  }
  return false;
}

export const allowedDatesRecurring = (date: any, maxBookAhead: number) => {
  const day = moment(date).startOf('day');
  const today = moment().startOf('day');
  if (!day.isSameOrAfter(today)) return false;
  if (!maxBookAhead) return true;
  
  return day.isSameOrBefore(today.clone().add(maxBookAhead, "weeks"));
};

export function useInactivityTimer(
  bookingId: () => string | string[],
  showPopup: Ref<boolean>,
  onTimeout: () => Promise<void>,
  canShowPopup?: () => boolean
) {
  let inactivityTimer: ReturnType<typeof setTimeout>;
  let inactivity: any;
  const time = 2 * 60 * 1000; // 2 minutes

  const showInactivityPopup = () => {
    if (canShowPopup && !canShowPopup()) return;
    showPopup.value = true;
  };

  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(showInactivityPopup, time); // 2 minutes
  };

  const continueBooking = async() => {
    showPopup.value = false;
    resetInactivityTimer();
    // update booking(s) so that server will leave them alone for another 2 minutes
    const ids = bookingId();
    const idList = Array.isArray(ids) ? ids : ids ? [ids] : [];
    const result = await Promise.all(idList.map(id => pb.collection("bookings").update(id, { deletedReason: '' })));
  };

  const startInactivityTimer = () => {
    inactivity = setTimeout(async () => {
      showPopup.value = false;
      await onTimeout();
    }, time);
  };

  const clearInactivityTimer = () => {
    if (inactivity !== null) {
      clearTimeout(inactivity);
      inactivity = null;
    }
  };

  const clearPendingTimer = () => {
    showPopup.value = false;
    window.removeEventListener("mousemove", resetInactivityTimer);
    window.removeEventListener("keydown", resetInactivityTimer);
    window.removeEventListener("touchstart", resetInactivityTimer);
    window.removeEventListener("click", resetInactivityTimer);
    clearTimeout(inactivityTimer);
  };

  return {
    resetInactivityTimer,
    showInactivityPopup,
    continueBooking,
    startInactivityTimer,
    clearInactivityTimer,
    clearPendingTimer,
  };
}
